import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { value: "interested", label: "Interested" },
  { value: "meeting", label: "Meeting" },
  { value: "objection", label: "Objection" },
  { value: "ooo", label: "Out of Office" },
  { value: "referral", label: "Referral" },
  { value: "unsubscribe", label: "Unsubscribe" },
];

export function LogReplyDialog({ open, onOpenChange, onSaved, presetContactId = "", presetContactLabel = "" }) {
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ contact_id: "", campaign_id: "", body: "", category: "interested" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    setForm((f) => ({ ...f, contact_id: presetContactId || "" }));
  }, [open, presetContactId]);

  const save = async () => {
    if (!form.contact_id) return toast.error("Select a contact");
    if (!form.body.trim()) return toast.error("Paste the reply text");
    setBusy(true);
    try {
      const payload = { ...form, campaign_id: form.campaign_id || null };
      const { data } = await api.post("/replies/log", payload);
      toast.success("Reply logged - sequence stopped, pipeline updated");
      onOpenChange(false);
      setForm({ contact_id: "", campaign_id: "", body: "", category: "interested" });
      onSaved?.(data);
    } catch {
      toast.error("Could not log reply");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="log-reply-dialog">
        <DialogHeader>
          <DialogTitle>Log a reply</DialogTitle>
          <DialogDescription>Record a reply you received elsewhere. This stops the sequence and moves the deal forward.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Contact</Label>
            <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="log-reply-contact"><SelectValue placeholder={presetContactLabel || "Select a contact"} /></SelectTrigger>
              <SelectContent>
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.company}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Campaign <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="log-reply-campaign"><SelectValue placeholder="Select a campaign" /></SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Raw reply text</Label>
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} className="mt-1.5" data-testid="log-reply-text" placeholder="Paste exactly what the prospect wrote…" />
          </div>
          <div>
            <Label>Classification</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5" data-testid="log-reply-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="log-reply-save">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Log reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
